import React, { useState, useRef } from 'react';
import {
  Box, Button, IconButton, Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useTranslation } from 'react-i18next';
import useStyles from './styles';

const ImageUpload = ({
  value, onChange, label, hint, resize = true, preserveAspectRatio = false,
  width = 300, height = 300,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const readFile = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const resizeImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (preserveAspectRatio) {
          const scale = Math.min(width / img.width, height / img.height);
          const drawWidth = Math.round(img.width * scale);
          const drawHeight = Math.round(img.height * scale);
          const offsetX = Math.round((width - drawWidth) / 2);
          const offsetY = Math.round((height - drawHeight) / 2);

          if (/jpe?g/.test(file.type)) {
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, width, height);
          }

          ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
        } else {
          ctx.drawImage(img, 0, 0, width, height);
        }

        resolve(canvas.toDataURL(file.type));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const processFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      onChange('Error');
      return;
    }

    setUploading(true);
    setFileName(file.name);

    try {
      const base64 = resize ? await resizeImage(file) : await readFile(file);
      onChange(base64);
    } catch (error) {
      onChange('Error');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    processFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    processFile(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleClear = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onChange('');
  };

  const getDisplayText = () => {
    if (fileName) return fileName;
    if (value && value.startsWith('http')) {
      const loadedImage = t('imageUploader.loadedImage');
      if (value.includes('?')) {
        return value.split('/').pop().split('?')[0] || loadedImage;
      }
      return value.split('/').pop() || loadedImage;
    }
    if (value && value.startsWith('data:')) {
      return t('imageUploader.selectedImage');
    }
    return null;
  };

  return (
    <Box>
      <input
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        id={`file-input-${label}`}
        ref={fileInputRef}
      />
      <Box
        className={`${classes.imageContainer} ${isDragging ? classes.dragginBorder : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {value ? (
          <Box>
            <Box className={classes.imageWrapper}>
              <img src={value} alt={label} className={classes.imagePreview} />
              <IconButton
                size="small"
                className={classes.clearButton}
                onClick={handleClear}
                aria-label={t('imageUploader.clearImage')}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="caption" display="block">
              {getDisplayText()}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="textSecondary">
              {t('imageUploader.bodyMessage')}
            </Typography>
            {hint && (
              <Typography variant="caption" color="textSecondary">
                {hint}
              </Typography>
            )}
          </Box>
        )}
        <label htmlFor={`file-input-${label}`}>
          <Button
            variant="outlined"
            component="span"
            disabled={uploading}
            className={classes.uploadButton}
          >
            {uploading ? t('imageUploader.uploading') : t('imageUploader.selectImage')}
          </Button>
        </label>
      </Box>
    </Box >
  );
};

export default ImageUpload;
